import React, { useState } from 'react';
import './SharedComponents.css';

/**
 * Reusable Work Experience Manager
 * Handles dynamic array of work experience entries
 * Used in both Register and EditProfile
 */

/* Theme-aware table cell styles */
const tdStyle = { backgroundColor: 'var(--surface-color)', color: 'var(--text-color)', borderColor: 'var(--border-color)' };
const thStyle = { backgroundColor: 'var(--hover-background)', color: 'var(--text-color)', borderColor: 'var(--border-color)' };

const WorkExperience = ({
  workExperience,
  setWorkExperience,
  isRequired = false,
  showValidation = false,
  errorMsg = '',
  setErrorMsg = () => {}
}) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const [newWorkExperience, setNewWorkExperience] = useState({
    status: '',
    workType: '',
    description: '',
    location: ''
  });

  const handleWorkExperienceChange = (e) => {
    const { name, value } = e.target;
    setNewWorkExperience(prev => ({ ...prev, [name]: value }));
  };

  const handleAddWorkExperience = () => {
    // Validation
    if (!newWorkExperience.status) {
      setErrorMsg("❌ Please select work status");
      return;
    }
    if (!newWorkExperience.description || newWorkExperience.description.trim().length < 5) {
      setErrorMsg("❌ Please enter work description (at least 5 characters)");
      return;
    }

    // Add or update
    const workEntry = {
      status: newWorkExperience.status,
      workType: newWorkExperience.workType,
      description: newWorkExperience.description.trim(),
      location: newWorkExperience.location?.trim() || ''
    };

    if (editingIndex !== null) {
      // Update existing
      const updated = [...workExperience];
      updated[editingIndex] = workEntry;
      setWorkExperience(updated);
      setEditingIndex(null);
    } else {
      // Add new
      setWorkExperience([...workExperience, workEntry]);
    }

    // Reset form
    setNewWorkExperience({
      status: '',
      workType: '',
      description: '',
      location: ''
    });
    setErrorMsg('');
  };

  const handleEditWorkExperience = (index) => {
    setNewWorkExperience(workExperience[index]);
    setEditingIndex(index);
  };

  const handleDeleteWorkExperience = (index) => {
    setWorkExperience(workExperience.filter((_, i) => i !== index));
    if (editingIndex === index) {
      setEditingIndex(null);
      setNewWorkExperience({
        status: '',
        workType: '',
        description: '',
        location: ''
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setNewWorkExperience({
      status: '',
      workType: '',
      description: '',
      location: ''
    });
  };

  return (
    <div className="mb-4">
      <h5 className="mt-4 mb-3 text-primary">
        💼 Work Experience {isRequired && <span className="text-danger">*</span>}
      </h5>

      {/* Add/Edit Work Experience Form */}
      <div className="mb-3">
        <div className="row mb-3">
          <div className="col-md-2">
            <label className="form-label">Work Status <span className="text-danger">*</span></label>
            <select
              className="form-control"
              name="status"
              value={newWorkExperience.status}
              onChange={handleWorkExperienceChange}
            >
              <option value="">Select Status</option>
              <option value="current">Current</option>
              <option value="past">Past</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Work Type</label>
            <select
              className="form-control"
              name="workType"
              value={newWorkExperience.workType}
              onChange={handleWorkExperienceChange}
            >
              <option value="">Select Type</option>
              <option value="accountant">Accountant</option>
              <option value="analyst">Analyst</option>
              <option value="artist">Artist</option>
              <option value="attorney">Attorney</option>
              <option value="consultant">Consultant</option>
              <option value="customer service">Customer Service</option>
              <option value="dentist">Dentist</option>
              <option value="designer">Designer</option>
              <option value="developer">Developer</option>
              <option value="doctor">Doctor</option>
              <option value="engineer">Engineer</option>
              <option value="entrepreneur">Entrepreneur</option>
              <option value="finance">Finance</option>
              <option value="freelancer">Freelancer</option>
              <option value="hr">HR</option>
              <option value="manager">Manager</option>
              <option value="marketing">Marketing</option>
              <option value="nurse">Nurse</option>
              <option value="operations">Operations</option>
              <option value="others">Others</option>
              <option value="pharma">Pharma</option>
              <option value="physical therapy">Physical Therapy</option>
              <option value="researcher">Researcher</option>
              <option value="sales">Sales</option>
              <option value="scientist">Scientist</option>
              <option value="software">Software</option>
              <option value="student">Student</option>
              <option value="teacher">Teacher</option>
              <option value="writer">Writer</option>
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Type of work & Industry <span className="text-danger">*</span></label>
            <input
              type="text"
              className="form-control"
              name="description"
              value={newWorkExperience.description}
              onChange={handleWorkExperienceChange}
              placeholder="e.g., Software Engineer in Tech Industry, Marketing Manager in Healthcare"
            />
          </div>

          <div className="col-md-2">
            <label className="form-label">Work Location</label>
            <input
              type="text"
              className="form-control"
              name="location"
              value={newWorkExperience.location}
              onChange={handleWorkExperienceChange}
              placeholder="e.g., Bangalore, San Francisco"
            />
          </div>

          <div className="col-md-2 d-flex align-items-end justify-content-center">
            <button
              type="button"
              className="btn btn-warning btn-square-action btn-work-add-action"
              onClick={handleAddWorkExperience}
            >
              <span className="btn-work-add-label">{editingIndex !== null ? 'Update Experience' : 'Add Experience'}</span>
            </button>
          </div>
        </div>

        {editingIndex !== null && (
          <div className="mt-2">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleCancelEdit}
            >
              Cancel Edit
            </button>
          </div>
        )}
      </div>

      {/* Saved Work Experience Entries */}
      {workExperience.length > 0 && (
        <div className="mb-3 table-container-scroll">
          <table className="table table-bordered table-stacked" style={{ backgroundColor: 'var(--surface-color, #fff)', borderColor: 'var(--border-color, #dee2e6)' }}>
            <thead style={{ backgroundColor: 'var(--hover-background)', borderColor: 'var(--border-color)' }}>
              <tr>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Work Type</th>
                <th style={thStyle}>Description</th>
                <th style={thStyle}>Location</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {workExperience.map((work, index) => (
                <tr key={index}>
                  <td data-label="Status" style={tdStyle}>
                    <span className={`badge ${work.status === 'current' ? 'bg-success' : 'bg-secondary'}`}>
                      {work.status}
                    </span>
                  </td>
                  <td data-label="Work Type" style={tdStyle}>
                    <span className="badge bg-info">{work.workType || 'Not specified'}</span>
                  </td>
                  <td data-label="Description" style={tdStyle}>{work.description}</td>
                  <td data-label="Location" style={tdStyle}>{work.location || '-'}</td>
                  <td data-label="Actions" style={tdStyle} className="actions-cell">
                    <button
                      type="button"
                      className="btn btn-sm btn-warning me-2"
                      onClick={() => handleEditWorkExperience(index)}
                      title="Edit"
                    >
                      <span className="btn-icon">✏️</span>
                      <span className="btn-text">Edit</span>
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteWorkExperience(index)}
                      title="Delete"
                    >
                      <span className="btn-icon">🗑️</span>
                      <span className="btn-text">Delete</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showValidation && isRequired && workExperience.length === 0 && (
        <small className="text-danger d-block mb-2">
          ⚠️ Please add at least one work experience entry
        </small>
      )}
    </div>
  );
};

export default WorkExperience;
